import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from '../config/apiconfig';
import {
    BarChart3Icon,
    UsersIcon,
    BriefcaseIcon,
    UserCheckIcon,
    SettingsIcon,
    FileTextIcon,
    CalendarIcon,
    ClockIcon,
    UserIcon,
    TargetIcon,
    CreditCardIcon,
    BellIcon,
    PlusIcon,
    SearchIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ChevronRightIcon,
    StarIcon,
    CheckCircleIcon,
    ThumbsUpIcon
} from 'lucide-react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import GoalsSection from "../components/GoalsSection";
import { useSidebar } from "../contexts/SidebarContext";
import {
    EmailTemplates,
    EventTemplates,
    BenefitTemplates,
    HolidayTemplates,
    CustomFields
} from '../components/TemplateComponents';
import { DocumentTemplates } from '../components/DocumentTemplates';
import StatusChangeModal from '../components/StatusChangeModal';

const RecruiterDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('overview');
    const [expandedSections, setExpandedSections] = useState({
        hire: true,
        manage: false,
        retain: false,
        settings: false,
        'settings-templates': false
    });
    const sidebar = useSidebar();
    const [activeFilter, setActiveFilter] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    // Approval gate: block all recruiter dashboard usage until approved
    const [approved, setApproved] = useState(false);
    const [active, setActive] = useState(true);
    const [checkedApproval, setCheckedApproval] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!user?.id) return;
            try {
                const res = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}?_=${Date.now()}`, { cache: 'no-store' });
                if (res.ok) {
                    const data = await res.json();
                    const ok = data?.approval_status === 'approved';
                    if (!cancelled) setApproved(!!ok);
                    if (!ok) {
                        navigate('/waiting-approval', { replace: true });
                        return;
                    }
                    const isActive = data?.account_status === 'active';
                    if (!cancelled) setActive(!!isActive);
                    if (!isActive) {
                        navigate('/inactive-account', { replace: true });
                        return;
                    }
                }
            } finally {
                if (!cancelled) setCheckedApproval(true);
            }
        })();
        return () => { cancelled = true; };
    }, [user?.id]);

    useEffect(() => {
        if (!approved || !checkedApproval) return;
        if (user?.id) {
            fetchApplications();
            fetchJobs();
            fetchProfileData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, approved, checkedApproval]);

    // Fetch reviews only when the reviews tab is active and base checks passed
    useEffect(() => {
        if (!approved || !checkedApproval) return;
        if (user?.id && activeTab === 'reviews') {
            fetchRecruiterReviews();
            fetchReviewSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, user?.id, approved, checkedApproval]);

    const [applications, setApplications] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [profileData, setProfileData] = useState({});
    const [profileLoading, setProfileLoading] = useState(false);
    const [profilePicture, setProfilePicture] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isEditingCompany, setIsEditingCompany] = useState(false);
    const [companyLogo, setCompanyLogo] = useState(null);
    const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
    const [jobFilters, setJobFilters] = useState({
        title: '',
        location: '',
        status: 'All',
    });
    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [jobTitleFilter, setJobTitleFilter] = useState('');
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [recruiterReviews, setRecruiterReviews] = useState([]);
    const [reviewSummary, setReviewSummary] = useState(null);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    // Cluster/Unit display state
    const [clusterName, setClusterName] = useState('');
    const [unitName, setUnitName] = useState('');
    const [unitAddress, setUnitAddress] = useState('');

    // Status change modal state
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [statusModalAppId, setStatusModalAppId] = useState(null);
    const [statusModalNewStatus, setStatusModalNewStatus] = useState('');

    // Initialize tab from URL hash on first mount
    useEffect(() => {
        const initial = (location.hash || '').replace('#', '');
        if (initial) setActiveTab(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update activeTab if URL hash changes (e.g., back/forward navigation)
    useEffect(() => {
        const hash = (location.hash || '').replace('#', '');
        if (hash && hash !== activeTab) {
            setActiveTab(hash);
        }
    }, [location.hash]);

    // Note: We no longer reflect activeTab back into the URL to avoid fighting browser back/forward navigation.

    const uniqueJobTitles = [...new Set(applications.map(app => app.job_title))].filter(Boolean);

    const filteredApplications = useMemo(() => {
        let filtered = applications.filter(application => {
            if (nameFilter && !application.applicant_name.toLowerCase().includes(nameFilter.toLowerCase()) && 
                !application.applicant_email.toLowerCase().includes(nameFilter.toLowerCase())) {
                return false;
            }
            if (statusFilter && application.status !== statusFilter) {
                return false;
            }
            if (jobTitleFilter && application.job_title !== jobTitleFilter) {
                return false;
            }
            if (dateFromFilter || dateToFilter) {
                const applicationDate = new Date(application.application_date);
                if (dateFromFilter) {
                    const fromDate = new Date(dateFromFilter);
                    if (applicationDate < fromDate) return false;
                }
                if (dateToFilter) {
                    const toDate = new Date(dateToFilter);
                    if (applicationDate > toDate) return false;
                }
            }
            return true;
        });
        
        if (sortField) {
            filtered.sort((a, b) => {
                let aValue, bValue;
                switch (sortField) {
                    case 'name':
                        aValue = a.applicant_name.toLowerCase();
                        bValue = b.applicant_name.toLowerCase();
                        break;
                    case 'status':
                        aValue = a.status;
                        bValue = b.status;
                        break;
                    case 'job_title':
                        aValue = a.job_title;
                        bValue = b.job_title;
                        break;
                    case 'application_date':
                        aValue = new Date(a.application_date);
                        bValue = new Date(b.application_date);
                        break;
                    default:
                        return 0;
                }
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return filtered;
    }, [applications, nameFilter, statusFilter, jobTitleFilter, dateFromFilter, dateToFilter, sortField, sortDirection]);

    const clearAllFilters = () => {
        setNameFilter('');
        setStatusFilter('');
        setJobTitleFilter('');
        setDateFromFilter('');
        setDateToFilter('');
        setSortField('');
        setSortDirection('asc');
    };

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSignatureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSignatureFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSignaturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadSignature = async () => {
        if (!signatureFile) return;
        const formData = new FormData();
        formData.append('digital_signature', signatureFile);
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter/signature/${user.id}`, {
                method: 'POST',
                body: formData
            });
            if (response.ok) {
                const result = await response.json();
                setSignaturePreview(`${API_BASE_URL}${result.url}?_=${Date.now()}`);
                setSignatureFile(null);
                fetchProfileData();
                alert('Signature uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading signature:', error);
            alert('Failed to upload signature');
        }
    };

    const fetchApplications = async () => {
        setApplicationsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/applications/recruiter/${user.id}`);
            if (response.ok) {
                const applicationData = await response.json();
                const applicationsWithUsers = applicationData.map(app => ({
                    ...app,
                    applicant_name: app.applicant_name || `User ${app.applicant_id}`,
                    applicant_email: app.applicant_email || 'No email'
                }));
                setApplications(applicationsWithUsers);
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
        } finally {
            setApplicationsLoading(false);
        }
    };

    const updateApplicationStatus = async (applicationId, newStatus) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: newStatus })
            });
            if (response.ok) {
                fetchApplications();
            }
        } catch (error) {
            console.error('Error updating application status:', error);
        }
    };

    const statusesRequiringTemplates = new Set(['Interviews','Scheduled','Offer','Hired','Rejected','Screening Disqualified']);
    const handleStatusChange = (application, newStatus) => {
        if (statusesRequiringTemplates.has(newStatus)) {
            setStatusModalAppId(application.id);
            setStatusModalNewStatus(newStatus);
            setStatusModalOpen(true);
        } else {
            updateApplicationStatus(application.id, newStatus);
        }
    };

    const fetchRecruiterReviews = async () => {
        setReviewsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter-reviews/recruiter/${user.id}`);
            const data = await response.json();
            if (data.success) {
                setRecruiterReviews(data.reviews);
            }
        } catch (error) {
            console.error('Error fetching recruiter reviews:', error);
        } finally {
            setReviewsLoading(false);
        }
    };

    const fetchReviewSummary = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter-reviews/summary/${user.id}`);
            const data = await response.json();
            if (data.success) {
                setReviewSummary(data.summary);
            }
        } catch (error) {
            console.error('Error fetching review summary:', error);
        }
    };

    const fetchProfileData = async () => {
        setProfileLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}?_=${Date.now()}`);
            if (response.ok) {
                const data = await response.json();
                setProfileData(data);
                if (data.profile_picture) {
                    setPreviewImage(`${API_BASE_URL}${data.profile_picture}?_=${Date.now()}`);
                }
                if (data.digital_signature) {
                    setSignaturePreview(`${API_BASE_URL}${data.digital_signature}?_=${Date.now()}`);
                }
                if (data.company_id) {
                    setCompanyLogoPreview(`${API_BASE_URL}/api/companies/${data.company_id}/logo?_=${Date.now()}`);
                }
                // Prefer names provided by backend directly
                setClusterName(data.cluster_name || '');
                setUnitName(data.unit_name || '');
                setUnitAddress(data.unit_address || '');
                // Fallback: Resolve cluster and unit names if IDs exist
                try {
                    if (data.company_id && (data.cluster_id || data.unit_id)) {
                        const [clRes, unRes] = await Promise.all([
                            fetch(`${API_BASE_URL}/api/companies/${data.company_id}/clusters`),
                            fetch(`${API_BASE_URL}/api/companies/${data.company_id}/units`),
                        ]);
                        const clusters = clRes.ok ? await clRes.json() : [];
                        const units = unRes.ok ? await unRes.json() : [];
                        if (!data.cluster_name && data.cluster_id) {
                            const c = clusters.find(c => String(c.id) === String(data.cluster_id));
                            setClusterName(c?.name || '');
                        }
                        if (!data.unit_name && data.unit_id) {
                            const u = units.find(u => String(u.id) === String(data.unit_id));
                            setUnitName(u?.name || '');
                            if (!data.unit_address) setUnitAddress(u?.address || '');
                        }
                    }
                } catch {}
            }
        } catch (error) {
            console.error('Error fetching profile data:', error);
        } finally {
            setProfileLoading(false);
        }
    };

    const updateProfile = async (updatedData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData)
            });
            if (response.ok) {
                fetchProfileData();
                alert('Profile updated successfully!');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('Failed to update profile');
        }
    };

    const updateCompany = async (updatedData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/company/${profileData.company_id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData)
            });
            if (response.ok) {
                fetchProfileData();
                alert('Company details updated successfully!');
            }
        } catch (error) {
            console.error('Error updating company:', error);
            alert('Failed to update company details');
        }
    };

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePicture(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadProfilePicture = async () => {
        if (!profilePicture) return;
        const formData = new FormData();
        formData.append('profile_picture', profilePicture);
        try {
            const response = await fetch(`${API_BASE_URL}/api/recruiter/profile-picture/${user.id}`, {
                method: 'POST',
                body: formData
            });
            if (response.ok) {
                const result = await response.json();
                setPreviewImage(`${API_BASE_URL}${result.url}`);
                setProfilePicture(null);
                fetchProfileData();
                alert('Profile picture updated successfully!');
            }
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            alert('Failed to upload profile picture');
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleCreateJob = () => {
        navigate('/create-job');
    };

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-postings/recruiter/${user.id}`);
            if (response.ok) {
                const jobData = await response.json();
                setJobs(jobData);
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateJobStatus = async (jobId, newStatus) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/job-postings/${jobId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: newStatus,
                    posted_by: user.id
                })
            });
            if (response.ok) {
                fetchJobs();
            }
        } catch (error) {
            console.error('Error updating job status:', error);
        }
    };

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const toggleFilterDropdown = (filterName) => {
        setActiveFilter(activeFilter === filterName ? null : filterName);
    };

    const getFilterOptions = (key) => {
        switch (key) {
            case 'title':
                return [...new Set(jobs.map(job => job.title))].sort();
            case 'location':
                return [...new Set(jobs.map(job => job.location))].sort();
            case 'status':
                return ['Active', 'Closed'];
            case 'post_date':
                return ['Today', 'This Week', 'This Month', 'Older'];
            default:
                return [];
        }
    };

    const handleCompanyLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCompanyLogo(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setCompanyLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadCompanyLogo = async () => {
        if (!companyLogo) return;
        const formData = new FormData();
        formData.append('logo', companyLogo);
        try {
            const response = await fetch(`${API_BASE_URL}/api/companies/${profileData.company_id}/logo`, {
                method: 'POST',
                body: formData
            });
            if (response.ok) {
                const result = await response.json();
                setCompanyLogoPreview(`${API_BASE_URL}${result.url}?_=${Date.now()}`);
                setCompanyLogo(null);
                fetchProfileData();
                alert('Company logo updated successfully!');
            }
        } catch (error) {
            console.error('Error uploading company logo:', error);
            alert('Failed to upload company logo');
        }
    };

    const sidebarItems = [
        {
            id: 'overview',
            label: 'Overview',
            icon: BarChart3Icon,
            section: 'main'
        },
        {
            section: 'hire',
            label: 'Hire',
            icon: BriefcaseIcon,
            items: [
                { id: 'jobs', label: 'Jobs', icon: BriefcaseIcon },
                { id: 'candidates', label: 'Candidates', icon: UsersIcon },
                { id: 'assessments', label: 'Assessments', icon: FileTextIcon, badge: true }
            ]
        },
        {
            section: 'retain',
            label: 'Retain',
            icon: TargetIcon,
            items: [
                { id: 'reviews', label: 'Reviews', icon: FileTextIcon },
                { id: 'goals', label: 'Goals', icon: TargetIcon }
            ]
        },
        {
            section: 'settings',
            label: 'Settings',
            icon: SettingsIcon,
            items: [
                { id: 'general', label: 'General', icon: SettingsIcon },
                { 
                    id: 'templates', 
                    label: 'Templates', 
                    icon: FileTextIcon,
                    items: [
                        { id: 'document-templates', label: 'Documents', icon: FileTextIcon },
                        { id: 'email-templates', label: 'Emails', icon: FileTextIcon },
                        { id: 'event-templates', label: 'Events', icon: CalendarIcon },
                        { id: 'benefit-templates', label: 'Benefits', icon: CreditCardIcon },
                        { id: 'holiday-templates', label: 'Holidays', icon: CalendarIcon },
                        { id: 'custom-fields', label: 'Custom Fields', icon: FileTextIcon }
                    ]
                },
                { id: 'profile', label: 'Profile', icon: UserIcon },
            ]
        }
    ];

    const filteredJobs = jobs.filter(job => {
        return (
            job.title.toLowerCase().includes(jobFilters.title.toLowerCase()) &&
            job.location.toLowerCase().includes(jobFilters.location.toLowerCase()) &&
            (jobFilters.status === 'All' || job.status === jobFilters.status)
        );
    });

    if (!checkedApproval) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header />
                <main className="flex-1 container mx-auto p-6"><div className="text-gray-600">Loading...</div></main>
                <Footer />
            </div>
        );
    }
    if (!approved) return null;

    const renderMainContent = () => {
        switch (activeTab) {
            case 'overview':
                const statusOrder = ['Screening', 'Qualified', 'Screening Disqualified', 'Interviews', 'Shortlisted', 'Offer', 'Hired', 'Offer Declined', 'Rejected'];
                const pipelineData = applications.reduce((acc, app) => {
                    acc[app.status] = (acc[app.status] || 0) + 1;
                    return acc;
                }, {});
                
                statusOrder.forEach(status => {
                    if (!pipelineData[status]) {
                        pipelineData[status] = 0;
                    }
                });

                const last30Days = new Date();
                last30Days.setDate(last30Days.getDate() - 30);
                const recentApplicationsCount = applications.filter(app => {
                    const appDate = new Date(app.application_date);
                    return appDate >= last30Days;
                }).length;

                const viewsData = jobs.reduce((total, job) => total + (job.views || 0), 0);
                const applicationsCount = applications.length;
                const conversionRate = viewsData > 0 ? (applicationsCount / viewsData * 100).toFixed(1) : 0;
                const maxCandidateVolume = Math.max(recentApplicationsCount, 10);
                const candidateVolumePercentage = (recentApplicationsCount / maxCandidateVolume) * 100;

                return (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <span>Last 30 days</span>
                                <ChevronDownIcon className="w-4 h-4" />
                            </div>
                        </div>
                        
                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Open Jobs</p>
                                        <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2 text-gray-900">{jobs.filter(job => job.status === 'Active').length}</p>
                                    </div>
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <BriefcaseIcon className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Total Candidates</p>
                                        <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2 text-gray-900">{applications.length}</p>
                                    </div>
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <UsersIcon className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">Conversion Rate</p>
                                        <p className="text-2xl md:text-3xl font-bold mt-1 md:mt-2 text-gray-900">{conversionRate}%</p>
                                    </div>
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                        <TargetIcon className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Hiring Pipeline Bar Chart */}
                        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200">
                            <div className="flex justify-between items-center mb-4 md:mb-6">
                                <div>
                                    <h2 className="text-lg md:text-xl font-semibold text-gray-900">Hiring Pipeline</h2>
                                    <p className="text-sm md:text-base text-gray-500 mt-1">{applications.length} total candidates</p>
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="flex items-end justify-between space-x-1 md:space-x-4 h-48 md:h-64 px-2 md:px-4 overflow-x-auto">
                                    {statusOrder.map((status) => {
                                        const count = pipelineData[status] || 0;
                                        const maxCount = Math.max(...Object.values(pipelineData), 1);
                                        const heightPercentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                        
                                        const statusColors = {
                                            'Screening': 'bg-gray-400',
                                            'Qualified': 'bg-blue-400',
                                            'Screening Disqualified': 'bg-red-400',
                                            'Interviews': 'bg-purple-400',
                                            'Shortlisted': 'bg-yellow-400',
                                            'Offer': 'bg-green-400',
                                            'Hired': 'bg-green-600',
                                            'Offer Declined': 'bg-orange-400',
                                            'Rejected': 'bg-red-600'
                                        };

                                        return (
                                            <div key={status} className="flex-1 flex flex-col items-center group min-w-[50px]">
                                                <div className="w-full flex justify-center mb-2 md:mb-3">
                                                    <div 
                                                        className={`w-10 md:w-16 ${statusColors[status]} rounded-t-lg shadow-sm transition-all duration-700 ease-out transform group-hover:scale-105 relative`}
                                                        style={{ 
                                                            height: `${Math.max(heightPercentage, 8)}%`,
                                                            minHeight: count > 0 ? '20px' : '8px'
                                                        }}
                                                    >
                                                        {count > 0 && (
                                                            <div className="absolute -top-6 md:-top-8 left-1/2 transform -translate-x-1/2">
                                                                <span className="bg-gray-800 text-white text-xs px-1 md:px-2 py-0.5 md:py-1 rounded font-medium">
                                                                    {count}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center">
                                                    <div className="text-xs md:text-sm font-medium text-gray-700 mb-1">
                                                        {status.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {count} candidate{count !== 1 ? 's' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="absolute bottom-0 left-2 md:left-4 right-2 md:right-4 h-px bg-gray-200"></div>
                            </div>
                        </div>

                        {/* Bottom Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                            {/* Candidate Volume Meter */}
                            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200">
                                <div className="flex justify-between items-center mb-4 md:mb-6">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">Candidate Volume</h3>
                                    <div className="flex items-center space-x-2 text-xs md:text-sm text-gray-500">
                                        <span>Last 30 days</span>
                                    </div>
                                </div>
                                
                                <div className="flex items-center justify-center">
                                    <div className="relative w-32 h-32 md:w-40 md:h-40">
                                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                            <circle
                                                cx="50"
                                                cy="50"
                                                r="40"
                                                fill="none"
                                                stroke="#f3f4f6"
                                                strokeWidth="8"
                                            />
                                            <circle
                                                cx="50"
                                                cy="50"
                                                r="40"
                                                fill="none"
                                                stroke="#3b82f6"
                                                strokeWidth="8"
                                                strokeLinecap="round"
                                                strokeDasharray={`${candidateVolumePercentage * 2.51}, 251`}
                                                className="transition-all duration-1000 ease-out"
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className="text-xl md:text-2xl font-bold text-gray-900">{recentApplicationsCount}</span>
                                            <span className="text-xs md:text-sm text-gray-500">Candidates</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-gray-200">
                                <h3 className="text-base md:text-lg font-semibold mb-4 md:mb-6">Quick Actions</h3>
                                <div className="space-y-2 md:space-y-3">
                                    <button 
                                        onClick={handleCreateJob}
                                        className="w-full flex items-center justify-between p-3 md:p-4 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150 rounded-lg transition-all duration-200 group border border-blue-200"
                                    >
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-500 rounded-lg flex items-center justify-center mr-2 md:mr-3 group-hover:bg-blue-600 transition-colors">
                                                <BriefcaseIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                                            </div>
                                            <span className="font-medium text-gray-900">Create New Job</span>
                                        </div>
                                        <ChevronRightIcon className="w-4 h-4 md:w-5 md:h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                                    </button>
                                    
                                    <button 
                                        onClick={() => setActiveTab('candidates')}
                                        className="w-full flex items-center justify-between p-3 md:p-4 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-150 rounded-lg transition-all duration-200 group border border-green-200"
                                    >
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500 rounded-lg flex items-center justify-center mr-2 md:mr-3 group-hover:bg-green-600 transition-colors">
                                                <UsersIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                                            </div>
                                            <span className="font-medium text-gray-900">Review Candidates</span>
                                        </div>
                                        <ChevronRightIcon className="w-4 h-4 md:w-5 md:h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                                    </button>
                                    
                                    <button 
                                        onClick={() => setActiveTab('general')}
                                        className="w-full flex items-center justify-between p-3 md:p-4 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-150 rounded-lg transition-all duration-200 group border border-purple-200"
                                    >
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-2 md:mr-3 group-hover:bg-purple-600 transition-colors">
                                                <SettingsIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                                            </div>
                                            <span className="font-medium text-gray-900">Company Settings</span>
                                        </div>
                                        <ChevronRightIcon className="w-4 h-4 md:w-5 md:h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            
            case 'jobs':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0">
                                <h1 className="text-xl md:text-2xl font-bold text-gray-900">Jobs</h1>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs md:text-sm min-w-0">
                                    <span className="font-medium">{jobs.length} jobs</span>
                                    <span className="text-green-600">{jobs.filter(job => job.status === 'Active').length} active</span>
                                    <span className="text-gray-500">{jobs.filter(job => job.status === 'Closed').length} closed</span>
                                </div>
                            </div>
                            <button 
                                onClick={handleCreateJob}
                                className="w-full md:w-auto bg-green-600 text-white px-3 py-2 md:px-4 md:py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                            >
                                <PlusIcon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                                <span className="text-sm md:text-base">Create new job</span>
                            </button>
                        </div>
                        
                        {/* Enhanced Filter Controls */}
                        <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm border">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Job Title</label>
                                    <input
                                        type="text"
                                        placeholder="Filter by title"
                                        value={jobFilters.title}
                                        onChange={(e) => setJobFilters({...jobFilters, title: e.target.value})}
                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xs md:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Location</label>
                                    <select
                                        value={jobFilters.location}
                                        onChange={(e) => setJobFilters({...jobFilters, location: e.target.value})}
                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xs md:text-sm"
                                    >
                                        <option value="">All Locations</option>
                                        {[...new Set(jobs.map(job => job.location))].map(location => (
                                            <option key={location} value={location}>{location}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={jobFilters.status}
                                        onChange={(e) => setJobFilters({...jobFilters, status: e.target.value})}
                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xs md:text-sm"
                                    >
                                        <option value="All">All Statuses</option>
                                        <option value="Active">Active</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Posted Date</label>
                                    <select
                                        value={jobFilters.postDate}
                                        onChange={(e) => setJobFilters({...jobFilters, postDate: e.target.value})}
                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xs md:text-sm"
                                    >
                                        <option value="">All Dates</option>
                                        <option value="today">Today</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="older">Older</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                            {loading ? (
                                <div className="p-6 md:p-8 text-center">Loading...</div>
                            ) : filteredJobs.length === 0 ? (
                                <div className="p-6 md:p-8 text-center">
                                    <div className="w-16 h-16 md:w-24 md:h-24 mx-auto mb-3 md:mb-4 bg-orange-100 rounded-lg flex items-center justify-center">
                                        <BriefcaseIcon className="w-8 h-8 md:w-12 md:h-12 text-orange-600" />
                                    </div>
                                    <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">No jobs match your filters</p>
                                    <button 
                                        onClick={() => setJobFilters({
                                            title: '', 
                                            location: '', 
                                            status: 'All',
                                            postDate: ''
                                        })}
                                        className="bg-blue-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg hover:bg-blue-700 text-sm md:text-base"
                                    >
                                        Clear filters
                                    </button>
                                </div>
                            ) : (
                                <div className="w-full min-w-0 overflow-x-auto">
                                    <table className="w-full min-w-[800px]">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            {[
                                            { key: 'title', label: 'Title' },
                                            { key: 'location', label: 'Location' },
                                            { key: 'candidates', label: 'Candidates' },
                                            { key: 'status', label: 'Status' },
                                            { key: 'post_date', label: 'Posted Date' },
                                            { key: 'closing_date', label: 'Closing Date' },
                                            { key: 'actions', label: 'Actions' }
                                            ].map((column) => (
                                            <th key={column.key} className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                <div className="flex items-center">
                                                {column.label}
                                                {column.key !== 'actions' && (
                                                    <div className="ml-1 relative">
                                                    <button 
                                                        onClick={() => toggleFilterDropdown(column.key)}
                                                        className="focus:outline-none"
                                                    >
                                                        <ChevronDownIcon className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                                                    </button>
                                                    
                                                    {activeFilter === column.key && (
                                                        <div className="absolute z-10 mt-1 w-36 md:w-48 bg-white shadow-lg rounded-md py-1">
                                                        <div className="px-3 py-1 md:px-4 md:py-2 text-xs text-gray-500 border-b">Filter by {column.label}</div>
                                                        {getFilterOptions(column.key).map(option => (
                                                            <button
                                                            key={option}
                                                            className="block w-full text-left px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-100"
                                                            onClick={() => {
                                                                setJobFilters({ ...jobFilters, [column.key]: option });
                                                                setActiveFilter(null);
                                                            }}
                                                            >
                                                            {option}
                                                            </button>
                                                        ))}
                                                        <button
                                                            className="block w-full text-left px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-100 border-t"
                                                            onClick={() => {
                                                            setJobFilters({ ...jobFilters, [column.key]: '' });
                                                            setActiveFilter(null);
                                                            }}
                                                        >
                                                            Clear filter
                                                        </button>
                                                        </div>
                                                    )}
                                                    </div>
                                                )}
                                                </div>
                                            </th>
                                            ))}
                                        </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-200">
                                            {filteredJobs
                                                .filter(job => {
                                                    if (!jobFilters.postDate) return true;
                                                    
                                                    const postDate = new Date(job.post_date);
                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);
                                                    
                                                    switch(jobFilters.postDate) {
                                                        case 'today':
                                                            return postDate >= today;
                                                        case 'week':
                                                            const weekAgo = new Date(today);
                                                            weekAgo.setDate(weekAgo.getDate() - 7);
                                                            return postDate >= weekAgo;
                                                        case 'month':
                                                            const monthAgo = new Date(today);
                                                            monthAgo.setMonth(monthAgo.getMonth() - 1);
                                                            return postDate >= monthAgo;
                                                        case 'older':
                                                            const monthAgoForOlder = new Date(today);
                                                            monthAgoForOlder.setMonth(monthAgoForOlder.getMonth() - 1);
                                                            return postDate < monthAgoForOlder;
                                                        default:
                                                            return true;
                                                    }
                                                })
                                                .map((job) => {
                                                    const jobApplications = applications.filter(app => app.job_id === job.id);
                                                    return (
                                                        <tr key={job.id}>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap">
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        <Link to={`/job-details/${job.id}`} className="hover:text-blue-600">
                                                                            {job.title}
                                                                        </Link>
                                                                    </div>
                                                                    <div className="text-xs md:text-sm text-gray-500">{job.salary ? `$${job.salary}` : 'Salary not specified'}</div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-900">{job.location}</td>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <Link to={`/recruiter/job-candidates/${job.id}`}>
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer transition-colors">
                                                                        {jobApplications.length} candidates
                                                                    </span>
                                                                </Link>
                                                            </td>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap">
                                                                <span className={`inline-flex px-2 py-0.5 md:px-2 md:py-1 text-xs font-semibold rounded-full ${
                                                                    job.status === 'Active' 
                                                                    ? 'bg-green-100 text-green-800' 
                                                                    : 'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                    {job.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {new Date(job.post_date).toLocaleDateString()}
                                                            </td>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {new Date(job.closing_date).toLocaleDateString()}
                                                            </td>
                                                            <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">
                                                                <div className="flex space-x-1 md:space-x-2">
                                                                    <select
                                                                        value={job.status}
                                                                        onChange={(e) => updateJobStatus(job.id, e.target.value)}
                                                                        className="border border-gray-300 rounded px-1 py-0.5 md:px-2 md:py-1 text-xs md:text-sm"
                                                                    >
                                                                        <option value="Active">Active</option>
                                                                        <option value="Closed">Closed</option>
                                                                    </select>
                                                                    <Link 
                                                                        to={`/edit-job/${job.id}`}
                                                                        className="text-blue-600 hover:text-blue-800 px-1 py-0.5 md:px-2 md:py-1 text-xs md:text-sm"
                                                                    >
                                                                        Edit
                                                                    </Link>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                );
            
            case 'candidates':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0">
                                <h1 className="text-xl md:text-2xl font-bold text-gray-900">Candidates</h1>
                                <span className="text-xs md:text-sm text-gray-500">{applications.length} candidates</span>
                            </div>
                            <button className="w-full md:w-auto bg-green-600 text-white px-3 py-2 md:px-4 md:py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center">
                                <PlusIcon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                                <span className="text-sm md:text-base">Save current search</span>
                            </button>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-sm border">
                            {/* Enhanced Filter Section - Single Line */}
                            <div className="p-3 md:p-4 border-b space-y-3 md:space-y-4">
                                {/* All Filters in Single Row */}
                                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                                    {/* Search Input */}
                                    <div className="flex-1 min-w-[150px] md:min-w-64">
                                        <div className="relative">
                                            <SearchIcon className="absolute left-2 md:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 md:w-4 md:h-4" />
                                            <input
                                                type="text"
                                                placeholder="Search candidates..."
                                                className="w-full pl-7 md:pl-10 pr-2 md:pr-4 py-1.5 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
                                                value={nameFilter}
                                                onChange={(e) => setNameFilter(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Status Filter */}
                                    <select 
                                        className="px-2 py-1.5 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="Screening">Screening</option>
                                        <option value="Qualified">Qualified</option>
                                        <option value="Screening Disqualified">Screening Disqualified</option>
                                        <option value="Interviews">Interviews</option>
                                        <option value="Shortlisted">Shortlisted</option>
                                        <option value="Offer">Offer</option>
                                        <option value="Hired">Hired</option>
                                        <option value="Offer Declined">Offer Declined</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                    
                                    {/* Job Title Filter */}
                                    <select 
                                        className="px-2 py-1.5 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                                        value={jobTitleFilter}
                                        onChange={(e) => setJobTitleFilter(e.target.value)}
                                    >
                                        <option value="">All Positions</option>
                                        {uniqueJobTitles.map(title => (
                                            <option key={title} value={title}>{title}</option>
                                        ))}
                                    </select>
                                    
                                    {/* Date From Filter */}
                                    <div className="flex items-center space-x-1 md:space-x-2">
                                        <label className="text-xs md:text-sm text-gray-600 whitespace-nowrap">From:</label>
                                        <input
                                            type="date"
                                            className="px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                                            value={dateFromFilter}
                                            onChange={(e) => setDateFromFilter(e.target.value)}
                                        />
                                    </div>
                                    
                                    {/* Date To Filter */}
                                    <div className="flex items-center space-x-1 md:space-x-2">
                                        <label className="text-xs md:text-sm text-gray-600 whitespace-nowrap">To:</label>
                                        <input
                                            type="date"
                                            className="px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                                            value={dateToFilter}
                                            onChange={(e) => setDateToFilter(e.target.value)}
                                        />
                                    </div>

                                    
                                    {/* Clear All Button */}
                                    <button 
                                        className="text-blue-600 hover:text-blue-700 whitespace-nowrap text-xs md:text-sm"
                                        onClick={clearAllFilters}
                                    >
                                        Clear all
                                    </button>
                                </div>
                                
                                {/* Active Filters Display */}
                                {(nameFilter || statusFilter || jobTitleFilter || dateFromFilter || dateToFilter) && (
                                    <div className="flex flex-wrap gap-1 md:gap-2 pt-2 border-t">
                                        <span className="text-xs md:text-sm text-gray-600">Active filters:</span>
                                        {nameFilter && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                Name: {nameFilter}
                                                <button 
                                                    className="ml-0.5 md:ml-1 text-blue-600 hover:text-blue-800"
                                                    onClick={() => setNameFilter('')}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        )}
                                        {statusFilter && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                                Status: {statusFilter}
                                                <button 
                                                    className="ml-0.5 md:ml-1 text-green-600 hover:text-green-800"
                                                    onClick={() => setStatusFilter('')}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        )}
                                        {jobTitleFilter && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                                                Position: {jobTitleFilter}
                                                <button 
                                                    className="ml-0.5 md:ml-1 text-purple-600 hover:text-purple-800"
                                                    onClick={() => setJobTitleFilter('')}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        )}
                                        {(dateFromFilter || dateToFilter) && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                                Date: {dateFromFilter || 'start'} - {dateToFilter || 'end'}
                                                <button 
                                                    className="ml-0.5 md:ml-1 text-orange-600 hover:text-orange-800"
                                                    onClick={() => {
                                                        setDateFromFilter('');
                                                        setDateToFilter('');
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {applicationsLoading ? (
                                <div className="p-6 md:p-8 text-center">Loading applications...</div>
                            ) : filteredApplications.length === 0 ? (
                                <div className="p-6 md:p-8 text-center">
                                    <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                        <UsersIcon className="w-5 h-5 md:w-8 md:h-8 text-gray-400" />
                                    </div>
                                    <p className="text-sm md:text-base text-gray-500">
                                        {applications.length === 0 ? 'No applications found' : 'No candidates match your filters'}
                                    </p>
                                    {applications.length > 0 && (
                                        <button 
                                            className="mt-1 md:mt-2 text-blue-600 hover:text-blue-800 text-sm md:text-base"
                                            onClick={clearAllFilters}
                                        >
                                            Clear all filters
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[800px]">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <div className="flex items-center space-x-1">
                                                        <span>Name</span>
                                                        <button 
                                                            className="text-gray-400 hover:text-gray-600"
                                                            onClick={() => toggleSort('name')}
                                                        >
                                                            {sortField === 'name' ? (
                                                                sortDirection === 'asc' ? '↑' : '↓'
                                                            ) : '↕'}
                                                        </button>
                                                    </div>
                                                </th>
                                                <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <div className="flex items-center space-x-1">
                                                        <span>Status</span>
                                                        <button 
                                                            className="text-gray-400 hover:text-gray-600"
                                                            onClick={() => toggleSort('status')}
                                                        >
                                                            {sortField === 'status' ? (
                                                                sortDirection === 'asc' ? '↑' : '↓'
                                                            ) : '↕'}
                                                        </button>
                                                    </div>
                                                </th>
                                                <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <div className="flex items-center space-x-1">
                                                        <span>Applied for</span>
                                                        <button 
                                                            className="text-gray-400 hover:text-gray-600"
                                                            onClick={() => toggleSort('job_title')}
                                                        >
                                                            {sortField === 'job_title' ? (
                                                                sortDirection === 'asc' ? '↑' : '↓'
                                                            ) : '↕'}
                                                        </button>
                                                    </div>
                                                </th>
                                                <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <div className="flex items-center space-x-1">
                                                        <span>Applied on</span>
                                                        <button 
                                                            className="text-gray-400 hover:text-gray-600"
                                                            onClick={() => toggleSort('application_date')}
                                                        >
                                                            {sortField === 'application_date' ? (
                                                                sortDirection === 'asc' ? '↑' : '↓'
                                                            ) : '↕'}
                                                        </button>
                                                    </div>
                                                </th>
                                                <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredApplications.map((application) => (
                                                <tr key={application.id}>
                                                    <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap">
                                                        <div>
                                                            <div 
                                                                className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                                                                onClick={() => navigate(`/candidate-details/${encodeURIComponent(application.applicant_email)}`)}
                                                            >
                                                                {application.applicant_name}
                                                            </div>
                                                            <div className="text-xs md:text-sm text-gray-500">
                                                                {application.applicant_email}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap">
                                                        <span className={`inline-flex px-2 py-0.5 md:px-2 md:py-1 text-xs font-semibold rounded-full ${
                                                            application.status === 'Screening' ? 'bg-yellow-100 text-yellow-800' :
                                                            application.status === 'Qualified' ? 'bg-blue-100 text-blue-800' :
                                                            application.status === 'Screening Disqualified' ? 'bg-red-100 text-red-800' :
                                                            application.status === 'Interviews' ? 'bg-purple-100 text-purple-800' :
                                                            application.status === 'Shortlisted' ? 'bg-indigo-100 text-indigo-800' :
                                                            application.status === 'Offer' ? 'bg-green-100 text-green-800' :
                                                            application.status === 'Hired' ? 'bg-green-200 text-green-900' :
                                                            application.status === 'Offer Declined' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {application.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {application.job_title}
                                                    </td>
                                                    <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {new Date(application.application_date).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <select
                                                            value={application.status}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleStatusChange(application, e.target.value);
                                                            }}
                                                            className="border border-gray-300 rounded px-1 py-0.5 md:px-2 md:py-1 text-xs md:text-sm"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <option value="Screening">Screening</option>
                                                            <option value="Qualified">Qualified</option>
                                                            <option value="Screening Disqualified">Screening Disqualified</option>
                                                            <option value="Interviews">Interviews</option>
                                                            <option value="Shortlisted">Shortlisted</option>
                                                            <option value="Offer">Offer</option>
                                                            <option value="Hired">Hired</option>
                                                            <option value="Offer Declined">Offer Declined</option>
                                                            <option value="Rejected">Rejected</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            
                            <div className="px-4 py-2 md:px-6 md:py-3 bg-gray-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <div className="flex items-center space-x-1 md:space-x-2">
                                    <span className="text-xs md:text-sm text-gray-700">
                                        Showing {filteredApplications.length} of {applications.length} candidates
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1 md:space-x-2">
                                    <button className="px-2 py-0.5 md:px-3 md:py-1 border border-gray-300 rounded text-xs md:text-sm">1</button>
                                </div>
                                <div className="flex items-center space-x-1 md:space-x-2">
                                    <span className="text-xs md:text-sm text-gray-700">20 / page</span>
                                    <ChevronDownIcon className="w-3 h-3 md:w-4 md:h-4 text-gray-400" />
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'profile':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Profile Settings</h1>
                            {!isEditingProfile && (
                                <button 
                                    onClick={() => setIsEditingProfile(true)}
                                    className="bg-blue-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg hover:bg-blue-700 text-sm md:text-base"
                                >
                                    Edit Profile
                                </button>
                            )}
                        </div>
                        
                        {profileLoading ? (
                            <div className="text-center py-6 md:py-8">Loading profile data...</div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-sm border">
                                <div className="p-4 md:p-6">
                                    <h2 className="text-lg font-semibold mb-4 md:mb-6">Account details</h2>
                                    
                                    {/* Profile Picture Section */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-6 md:mb-8">
                                        <div className="flex-shrink-0">
                                            <div className="w-16 h-16 md:w-20 md:h-20 bg-orange-500 rounded-full flex items-center justify-center overflow-hidden">
                                                {previewImage ? (
                                                    <img 
                                                        src={previewImage} 
                                                        alt="Profile" 
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = '';
                                                            e.target.parentElement.innerHTML = `
                                                                <span class="text-white text-xl md:text-2xl font-bold">
                                                                    ${profileData.first_name?.[0]}${profileData.last_name?.[0]}
                                                                </span>
                                                            `;
                                                        }}
                                                    />
                                                ) : profileData.profile_picture ? (
                                                    <img 
                                                        src={`${API_BASE_URL}${profileData.profile_picture}?t=${Date.now()}`} 
                                                        alt="Profile" 
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = '';
                                                            e.target.parentElement.innerHTML = `
                                                                <span class="text-white text-xl md:text-2xl font-bold">
                                                                    ${profileData.first_name?.[0]}${profileData.last_name?.[0]}
                                                                </span>
                                                            `;
                                                        }}
                                                    />
                                                ) : (
                                                    <span className="text-white text-xl md:text-2xl font-bold">
                                                        {profileData.first_name?.[0]}{profileData.last_name?.[0]}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {isEditingProfile && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                    Profile photo
                                                </label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleProfilePictureChange}
                                                    className="mb-1 md:mb-2 text-xs md:text-sm"
                                                />
                                                {profilePicture && (
                                                    <button
                                                        onClick={uploadProfilePicture}
                                                        className="bg-blue-600 text-white px-3 py-1 md:px-4 md:py-2 rounded-lg hover:bg-blue-700 text-xs md:text-sm"
                                                    >
                                                        Upload Picture
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Digital Signature (Optional) */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-6 md:mb-8">
                                        <div className="flex-shrink-0">
                                            <div className="w-40 h-20 md:w-56 md:h-24 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden border">
                                                {signaturePreview ? (
                                                    <img 
                                                        src={signaturePreview}
                                                        alt="Digital Signature"
                                                        className="max-h-full object-contain"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <span className="text-gray-500 text-xs md:text-sm">No signature added</span>
                                                )}
                                            </div>
                                        </div>
                                        {isEditingProfile && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                    Digital Signature (optional)
                                                </label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleSignatureChange}
                                                    className="mb-1 md:mb-2 text-xs md:text-sm"
                                                />
                                                {signatureFile && (
                                                    <button
                                                        onClick={uploadSignature}
                                                        className="bg-blue-600 text-white px-3 py-1 md:px-4 md:py-2 rounded-lg hover:bg-blue-700 text-xs md:text-sm"
                                                    >
                                                        Upload Signature
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Profile Form */}
                                    {isEditingProfile ? (
                                        <form onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            updateProfile({
                                                first_name: formData.get('first_name'),
                                                last_name: formData.get('last_name'),
                                                work_email: formData.get('work_email'),
                                                phone: formData.get('phone'),
                                                position: formData.get('position'),
                                                department: formData.get('department')
                                            });
                                            setIsEditingProfile(false);
                                        }}>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        First Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="first_name"
                                                        defaultValue={profileData.first_name || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Last Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="last_name"
                                                        defaultValue={profileData.last_name || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Work Email
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="work_email"
                                                        defaultValue={profileData.work_email || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Phone
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        defaultValue={profileData.phone || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Position
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="position"
                                                        defaultValue={profileData.position || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Department
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="department"
                                                        defaultValue={profileData.department || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-4 md:mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 md:space-x-4">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingProfile(false)}
                                                    className="bg-gray-200 text-gray-800 px-4 py-1.5 md:px-6 md:py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm md:text-base"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="bg-green-600 text-white px-4 py-1.5 md:px-6 md:py-2 rounded-lg hover:bg-green-700 transition-colors text-sm md:text-base"
                                                >
                                                    Save Changes
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className="space-y-3 md:space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">First Name</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.first_name || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Last Name</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.last_name || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Work Email</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.work_email || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Phone</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.phone || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Position</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.position || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Department</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.department || '-'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 'templates':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Templates</h1>
                            <div className="flex items-center space-x-2 text-xs md:text-sm text-gray-500">
                                <span>Last updated: Today</span>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-sm border">
                            <div className="p-3 md:p-4 border-b">
                                <div className="flex overflow-x-auto space-x-2 md:space-x-4 pb-1">
                                    <button 
                                        onClick={() => setActiveTab('email-templates')}
                                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap ${
                                            activeTab === 'email-templates' ? 'bg-gray-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Emails
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('document-templates')}
                                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap ${
                                            activeTab === 'document-templates' ? 'bg-gray-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Documents
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('event-templates')}
                                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap ${
                                            activeTab === 'event-templates' ? 'bg-gray-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Events
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('benefit-templates')}
                                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap ${
                                            activeTab === 'benefit-templates' ? 'bg-gray-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Benefits
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('holiday-templates')}
                                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap ${
                                            activeTab === 'holiday-templates' ? 'bg-gray-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Holidays
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('custom-fields')}
                                        className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg whitespace-nowrap ${
                                            activeTab === 'custom-fields' ? 'bg-gray-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Custom Fields
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-4 md:p-6">
                                {activeTab === 'email-templates' && <EmailTemplates />}
                                {activeTab === 'document-templates' && <DocumentTemplates />}
                                {activeTab === 'event-templates' && <EventTemplates />}
                                {activeTab === 'benefit-templates' && <BenefitTemplates />}
                                {activeTab === 'holiday-templates' && <HolidayTemplates />}
                                {activeTab === 'custom-fields' && <CustomFields />}
                            </div>
                        </div>
                    </div>
                );

            case 'email-templates':
                return <EmailTemplates />;
            case 'event-templates':
                return <EventTemplates />;
            case 'benefit-templates':
                return <BenefitTemplates />;
            case 'holiday-templates':
                return <HolidayTemplates />;
            case 'custom-fields':
                return <CustomFields />;
            case 'document-templates':
                return <DocumentTemplates />;

            case 'general':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Company Settings</h1>
                        </div>

                        {/* Cluster/Unit Info Section */}
                        {(clusterName || unitName) && (
                          <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
                            <h2 className="text-lg font-semibold mb-3 md:mb-4">Structure</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {clusterName && (
                                <div>
                                  <div className="text-sm text-gray-500">Cluster</div>
                                  <div className="text-gray-900 font-medium">{clusterName}</div>
                                </div>
                              )}
                              {unitName && (
                                <div>
                                  <div className="text-sm text-gray-500">Unit</div>
                                  <div className="text-gray-900 font-medium">{unitName}</div>
                                  {unitAddress ? (
                                    <div className="text-sm text-gray-500 mt-1">Address
                                      <div className="text-gray-900">{unitAddress}</div>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {profileLoading ? (
                            <div className="text-center py-6 md:py-8">Loading company data...</div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-sm border">
                                <div className="p-4 md:p-6">
                                    <h2 className="text-lg font-semibold mb-4 md:mb-6">Company details</h2>
                                    
                                    {/* Company Logo Section */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-6 md:mb-8">
                                    <div className="flex-shrink-0">
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-orange-500 rounded-lg flex items-center justify-center overflow-hidden">
                                            {companyLogoPreview ? (
                                                <img 
                                                    src={companyLogoPreview}
                                                    alt="Company Logo" 
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.onerror = null; // Prevent infinite error loop
                                                        e.target.style.display = 'none'; // Hide the broken image
                                                        if (e.target.parentElement) {
                                                            e.target.parentElement.innerHTML = `
                                                                <span class="text-white text-xl md:text-2xl font-bold">
                                                                    ${profileData.company_name?.[0] || 'C'}
                                                                </span>
                                                            `;
                                                        }
                                                    }}
                                                />
                                            ) : profileData.logo ? (
                                                <img 
                                                    src={`${API_BASE_URL}/api/companies/${profileData.company_id}/logo?_=${Date.now()}`}
                                                    alt="Company Logo" 
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.onerror = null; // Prevent infinite error loop
                                                        e.target.style.display = 'none'; // Hide the broken image
                                                        if (e.target.parentElement) {
                                                            e.target.parentElement.innerHTML = `
                                                                <span class="text-white text-xl md:text-2xl font-bold">
                                                                    ${profileData.company_name?.[0] || 'C'}
                                                                </span>
                                                            `;
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <span className="text-white text-xl md:text-2xl font-bold">
                                                    {profileData.company_name?.[0] || 'C'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                        {isEditingCompany && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                    Company Logo
                                                </label>
                                                <input 
                                                    type="file" 
                                                    accept="image/*" 
                                                    onChange={handleCompanyLogoChange} 
                                                    className="mb-1 md:mb-2 text-xs md:text-sm"
                                                />
                                                {companyLogo && (
                                                    <button
                                                        onClick={uploadCompanyLogo}
                                                        className="bg-blue-600 text-white px-3 py-1 md:px-4 md:py-2 rounded-lg hover:bg-blue-700 text-xs md:text-sm"
                                                    >
                                                        Upload Logo
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {isEditingCompany ? (
                                        <form onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            updateCompany({
                                                company_name: formData.get('company_name'),
                                                industry: formData.get('industry'),
                                                website: formData.get('website'),
                                                description: formData.get('description'),
                                                phone: formData.get('phone'),
                                                address: formData.get('address'),
                                                employee_count: formData.get('employee_count')
                                            });
                                            setIsEditingCompany(false);
                                        }}>
                                            <div className="space-y-4 md:space-y-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Company name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="company_name"
                                                        defaultValue={profileData.company_name || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Industry
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="industry"
                                                        defaultValue={profileData.industry || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        placeholder="e.g., Technology, Healthcare, Finance"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Number of employees
                                                    </label>
                                                    <select
                                                        name="employee_count"
                                                        defaultValue={profileData.employee_count || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    >
                                                        <option value="">Select size</option>
                                                        <option value="1-10">1-10 employees</option>
                                                        <option value="11-50">11-50 employees</option>
                                                        <option value="51-200">51-200 employees</option>
                                                        <option value="201-1000">201-1000 employees</option>
                                                        <option value="1000+">1000+ employees</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Description
                                                    </label>
                                                    <textarea
                                                        name="description"
                                                        rows={4}
                                                        defaultValue={profileData.description || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        placeholder="Tell us about your company..."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Website
                                                    </label>
                                                    <input
                                                        type="url"
                                                        name="website"
                                                        defaultValue={profileData.website || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        placeholder="https://www.example.com"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        Phone Number
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        defaultValue={profileData.phone || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                                                        HQ address
                                                    </label>
                                                    <textarea
                                                        name="address"
                                                        rows={3}
                                                        defaultValue={profileData.address || ''}
                                                        className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        placeholder="Company headquarters address"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mt-4 md:mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 md:space-x-4">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingCompany(false)}
                                                    className="bg-gray-200 text-gray-800 px-4 py-1.5 md:px-6 md:py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm md:text-base"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="bg-green-600 text-white px-4 py-1.5 md:px-6 md:py-2 rounded-lg hover:bg-green-700 transition-colors text-sm md:text-base"
                                                >
                                                    Save Changes
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className="space-y-3 md:space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Company Name</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.company_name || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Industry</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.industry || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Website</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.website || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Phone</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.company_phone || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Address</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.address || '-'}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Employees</label>
                                                    <p className="text-sm md:text-base text-gray-900">{profileData.employee_count || '-'}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs md:text-sm font-medium text-gray-500 mb-1">Description</label>
                                                <p className="text-sm md:text-base text-gray-900 whitespace-pre-line">{profileData.description || '-'}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 'assessments':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex justify-between items-center">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Assessments</h1>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow-sm border">
                            <div className="p-4 md:p-6">
                                <h3 className="text-lg font-semibold mb-3 md:mb-4">Candidate Applications</h3>
                                <div className="space-y-3 md:space-y-4">
                                    {applications.map((application) => (
                                        <div key={application.id} className="border rounded-lg p-3 md:p-4 hover:bg-gray-50">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 md:gap-4">
                                                <div className="flex-1">
                                                    <div className="flex flex-col sm:flex-row sm:items-center mb-1 md:mb-2 gap-1">
                                                        <h4 className="font-semibold text-gray-900">
                                                            {application.applicant_name || application.applicant_email}
                                                        </h4>
                                                        <span className={`px-2 py-0.5 md:px-2 md:py-1 text-xs font-semibold rounded-full ${
                                                            application.status === 'Hired' ? 'bg-green-100 text-green-800' :
                                                            application.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                                                            'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                            {application.status}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2">{application.job_title}</p>
                                                    <p className="text-xs text-gray-500">
                                                        Applied on {new Date(application.application_date).toLocaleDateString()}
                                                    </p>
                                                </div>
                                                <div className="sm:ml-4">
                                                    <Link
                                                        to={`/assessment/${application.id}`}
                                                        className="inline-flex items-center px-3 py-1 md:px-4 md:py-2 bg-blue-600 text-white text-xs md:text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                                    >
                                                        <FileTextIcon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                                                        Assess Candidate
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {applications.length === 0 && (
                                        <div className="text-center py-6 md:py-8 text-gray-500">
                                            <FileTextIcon className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-gray-300" />
                                            <p>No applications to assess yet.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'reviews':
                return (
                    <div className="space-y-4 md:space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Candidate Reviews</h2>
                        </div>

                        {/* Review Summary Cards */}
                        {reviewSummary && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
                                <div className="bg-white p-4 md:p-6 rounded-lg shadow border">
                                    <div className="flex items-center">
                                        <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg">
                                            <StarIcon className="w-4 h-4 md:w-6 md:h-6 text-blue-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600">Total Reviews</p>
                                            <p className="text-xl md:text-2xl font-bold text-gray-900">{reviewSummary.total_reviews || 0}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-4 md:p-6 rounded-lg shadow border">
                                    <div className="flex items-center">
                                        <div className="p-1.5 md:p-2 bg-yellow-100 rounded-lg">
                                            <StarIcon className="w-4 h-4 md:w-6 md:h-6 text-yellow-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600">Average Rating</p>
                                            <p className="text-xl md:text-2xl font-bold text-gray-900">
                                                {reviewSummary.average_rating ? parseFloat(reviewSummary.average_rating).toFixed(1) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-4 md:p-6 rounded-lg shadow border">
                                    <div className="flex items-center">
                                        <div className="p-1.5 md:p-2 bg-green-100 rounded-lg">
                                            <CheckCircleIcon className="w-4 h-4 md:w-6 md:h-6 text-green-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600">Positive Reviews</p>
                                            <p className="text-xl md:text-2xl font-bold text-gray-900">{reviewSummary.positive_reviews || 0}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-4 md:p-6 rounded-lg shadow border">
                                    <div className="flex items-center">
                                        <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg">
                                            <ThumbsUpIcon className="w-4 h-4 md:w-6 md:h-6 text-purple-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600">Recommendations</p>
                                            <p className="text-xl md:text-2xl font-bold text-gray-900">{reviewSummary.recommendations || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Reviews List */}
                        <div className="bg-white rounded-lg shadow border">
                            <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200">
                                <h3 className="text-base md:text-lg font-medium text-gray-900">Recent Reviews</h3>
                            </div>
                            
                            {reviewsLoading ? (
                                <div className="p-6 text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-2 text-sm text-gray-600">Loading reviews...</p>
                                </div>
                            ) : recruiterReviews.length === 0 ? (
                                <div className="p-6 text-center text-gray-500">
                                    <StarIcon className="w-8 h-8 md:w-12 md:h-12 text-gray-300 mx-auto mb-3 md:mb-4" />
                                    <p>No reviews yet</p>
                                    <p className="text-xs md:text-sm">Reviews from candidates will appear here</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {recruiterReviews.map((review) => (
                                        <div key={review.id} className="p-4 md:p-6">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex flex-col sm:flex-row sm:items-center mb-1 md:mb-2 gap-1 sm:gap-4">
                                                        <div className="flex items-center">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <StarIcon
                                                                    key={star}
                                                                    className={`w-3 h-3 md:w-4 md:h-4 ${
                                                                        star <= review.overall_rating
                                                                            ? 'text-yellow-400 fill-current'
                                                                            : 'text-gray-300'
                                                                    }`}
                                                                />
                                                            ))}
                                                            <span className="ml-1 md:ml-2 text-xs md:text-sm font-medium text-gray-900">
                                                                {review.overall_rating}/5
                                                            </span>
                                                        </div>
                                                        <span className="text-xs md:text-sm text-gray-500">
                                                            {new Date(review.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    
                                                    {review.review_title && (
                                                        <h4 className="text-base md:text-lg font-medium text-gray-900 mb-1 md:mb-2">
                                                            {review.review_title}
                                                        </h4>
                                                    )}
                                                    
                                                    <div className="mb-2 md:mb-3">
                                                        <p className="text-xs md:text-sm text-gray-600 mb-0.5">
                                                            <span className="font-medium">Job:</span> {review.job_title}
                                                        </p>
                                                        <p className="text-xs md:text-sm text-gray-600 mb-0.5">
                                                            <span className="font-medium">Company:</span> {review.company_name}
                                                        </p>
                                                        {!review.is_anonymous && (
                                                            <p className="text-xs md:text-sm text-gray-600">
                                                                <span className="font-medium">Candidate:</span> {review.candidate_first_name} {review.candidate_last_name}
                                                            </p>
                                                        )}
                                                    </div>
                                                    
                                                    {review.review_text && (
                                                        <p className="text-sm text-gray-700 mb-2 md:mb-3">{review.review_text}</p>
                                                    )}
                                                    
                                                    {(review.pros || review.cons) && (
                                                        <div className="mt-2 md:mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                                                            {review.pros && (
                                                                <div>
                                                                    <h5 className="text-xs md:text-sm font-medium text-green-700 mb-0.5">Pros:</h5>
                                                                    <p className="text-xs md:text-sm text-gray-600">{review.pros}</p>
                                                                </div>
                                                            )}
                                                            {review.cons && (
                                                                <div>
                                                                    <h5 className="text-xs md:text-sm font-medium text-red-700 mb-0.5">Cons:</h5>
                                                                    <p className="text-xs md:text-sm text-gray-600">{review.cons}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <div className="mt-2 md:mt-3 flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm">
                                                        {review.would_recommend && (
                                                            <span className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-xs font-medium ${
                                                                review.would_recommend === 'Yes'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : review.would_recommend === 'No'
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                            }`}>
                                                                Recommend: {review.would_recommend}
                                                            </span>
                                                        )}
                                                        {review.interview_difficulty && (
                                                            <span className="px-1.5 py-0.5 md:px-2 md:py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                                                                Difficulty: {review.interview_difficulty}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'goals':
                return (
                    <GoalsSection 
                        user={user} 
                        profileData={profileData}
                    />
                );

            default:
                return (
                    <div className="space-y-4 md:space-y-6">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-900 capitalize">{activeTab.replace('-', ' ')}</h1>
                        <div className="bg-white rounded-lg p-6 md:p-8 shadow-sm border text-center">
                            <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <SettingsIcon className="w-5 h-5 md:w-8 md:h-8 text-gray-400" />
                            </div>
                            <p className="text-sm md:text-base text-gray-500">This section is under development</p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden">
            <Header />
            <div className="flex flex-1">
                {/* Sidebar controlled by global context */}
                <Sidebar
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    isOpen={sidebar.isOpen}
                    onOverlayClick={sidebar.close}
                    onItemClick={sidebar.close}
                    items={sidebarItems}
                />
                
                {/* Main Content */}
                <div className={`flex-1 min-w-0 flex flex-col ${sidebar.isOpen ? 'md:ml-64' : ''}`}>
                    <main className="flex-1 min-w-0 p-4 md:p-6">
                        <div className="min-w-0 overflow-x-auto">
                            {renderMainContent()}
                        </div>
                    </main>
                    {/* Status Change Modal */}
                    <StatusChangeModal
                        visible={statusModalOpen}
                        onCancel={() => setStatusModalOpen(false)}
                        applicationId={statusModalAppId}
                        recruiterId={user?.id}
                        status={statusModalNewStatus}
                        onSubmitted={() => { setStatusModalOpen(false); fetchApplications(); }}
                    />
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default RecruiterDashboard;